import React, { useEffect, useState } from "react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { Megaphone, Plus, Loader2, BellRing } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import api from "@/lib/api";

const get = (url) => api.get(url).then((r) => r.data);
const post = (url, body) => api.post(url, body).then((r) => r.data);
function money(n) { return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; }

export default function Campaigns() {
  const [rows, setRows] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", goal_amount: "", program_id: "", description: "" });
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [r, p] = await Promise.all([get("/api/campaigns"), get("/api/programs")]);
      if (cancelled) return;
      setRows(r); setPrograms(p);
    })();
    return () => { cancelled = true; };
  }, []);

  const reload = async () => {
    const [r, p] = await Promise.all([get("/api/campaigns"), get("/api/programs")]);
    setRows(r); setPrograms(p);
  };

  const submit = async () => {
    try {
      setBusy(true);
      await post("/api/campaigns", { ...form, goal_amount: Number(form.goal_amount), program_id: form.program_id || null });
      toast({ title: "Campaign created" });
      setOpen(false); setForm({ name: "", goal_amount: "", program_id: "", description: "" });
      reload();
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader title="Campaigns" subtitle="Run drives with a goal, watch progress, and tie every gift back to its purpose.">
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const r = await api.post("/api/campaigns/check-milestones");
                if (r.data.triggered.length === 0) toast({ title: "No new milestones" });
                else toast({ title: `${r.data.triggered.length} milestone${r.data.triggered.length === 1 ? "" : "s"} reached!`, description: r.data.triggered.map((t) => `${t.campaign_name}: ${t.threshold}%`).join(" · ") });
              } catch (e) { toast({ title: "Check failed", description: e?.response?.data?.detail, variant: "destructive" }); }
            }}
            data-testid="campaigns-check-milestones"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm hover:bg-accent">
            <BellRing size={14} /> Check milestones
          </button>
          <button onClick={() => setOpen(true)} data-testid="campaigns-new" className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm font-medium">
            <Plus size={14} /> New campaign
          </button>
        </div>
      </PageHeader>
      <PageBody>
        {open && (
          <div className="rounded-xl border border-border bg-card p-5 mb-5 space-y-3">
            <h3 className="font-medium text-sm">New campaign</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-3 py-2 rounded border border-border bg-background" data-testid="campaigns-name" />
              <input type="number" placeholder="Goal $" value={form.goal_amount} onChange={(e) => setForm({ ...form, goal_amount: e.target.value })} className="px-3 py-2 rounded border border-border bg-background" data-testid="campaigns-goal" />
              <select value={form.program_id} onChange={(e) => setForm({ ...form, program_id: e.target.value })} className="px-3 py-2 rounded border border-border bg-background col-span-2">
                <option value="">-- Program (optional) --</option>
                {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <textarea placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="px-3 py-2 rounded border border-border bg-background col-span-2" />
            </div>
            <div className="flex gap-2">
              <button onClick={submit} disabled={busy || !form.name} data-testid="campaigns-submit" className="inline-flex items-center gap-1 px-3 py-2 rounded bg-primary text-primary-foreground text-sm">
                {busy && <Loader2 size={14} className="animate-spin" />} Create
              </button>
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded border border-border text-sm">Cancel</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.length === 0 && <div className="col-span-3 rounded-xl border border-border bg-card p-10 text-center text-muted-foreground">
            <Megaphone size={28} className="mx-auto mb-2 opacity-30" /> No campaigns yet — create one to start tracking a fundraising drive.
          </div>}
          {rows.map((c) => {
            const pct = c.goal_amount ? Math.min(100, Math.round((c.raised_amount / c.goal_amount) * 100)) : 0;
            return (
              <div key={c.id} className="rounded-xl border border-border bg-card p-5" data-testid={`campaign-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="font-medium truncate">{c.name}</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">{c.description?.slice(0, 80) || "—"}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${c.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-accent text-muted-foreground"}`}>{c.status}</span>
                </div>
                <div className="mt-4">
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="serif text-xl text-primary">{money(c.raised_amount)}</span>
                    <span className="text-xs text-muted-foreground">of {money(c.goal_amount)}</span>
                  </div>
                  <div className="h-1.5 bg-accent rounded-full mt-2 overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1.5 flex justify-between">
                    <span>{c.donor_count} donor{c.donor_count === 1 ? "" : "s"}</span>
                    <span>{pct}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </PageBody>
    </>
  );
}
